import { normalizeChecklistItems, type ChecklistTemplateItem } from './checklistTemplates'

export interface ServiceItemWorkFields {
  id?: string
  name?: string | null
  work_description?: string | null
  default_checklist?: Array<
    { label?: string; sort_order?: number; photo_required?: boolean } | string
  > | null
}

/** Contract service row with optional linked catalog item (as loaded on Job Create). */
export interface ContractServiceWorkSource {
  id?: string
  service_name?: string | null
  service_item_id?: string | null
  service_item?: ServiceItemWorkFields | ServiceItemWorkFields[] | null
}

export interface ContractLineSelectionForWork {
  description?: string | null
  service_item_id?: string | null
  contract_service_id?: string | null
}

function unwrapServiceItem(
  raw: ServiceItemWorkFields | ServiceItemWorkFields[] | null | undefined
): ServiceItemWorkFields | null {
  if (!raw) return null
  if (Array.isArray(raw)) return raw[0] || null
  return raw
}

function normalizeName(value: string | null | undefined): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

/** Rate-card / apartment-turnover jobs should not merge every service checklist on load. */
export function isRateCardStyleContract(contract: {
  service_type?: string | null
  document_data?: { pricingMode?: string | null } | null
  pricing_mode?: string | null
}): boolean {
  if (contract.service_type === 'apartment_turnovers') return true
  const mode =
    contract.document_data?.pricingMode ||
    (contract as { pricing_mode?: string | null }).pricing_mode
  return mode === 'rate_card'
}

/**
 * Resolve catalog work sources for the selected rate-card line items only.
 * Match order: service_item_id → contract_service_id → name vs service_name / item name.
 */
export function resolveWorkItemsForContractLineSelection(
  selected: ContractLineSelectionForWork[] | null | undefined,
  contractServices: ContractServiceWorkSource[] | null | undefined
): ServiceItemWorkFields[] {
  if (!Array.isArray(selected) || selected.length === 0) return []

  const services = Array.isArray(contractServices) ? contractServices : []
  const byServiceId = new Map<string, ContractServiceWorkSource>()
  const byItemId = new Map<string, ServiceItemWorkFields>()
  const byName = new Map<string, ServiceItemWorkFields>()

  for (const cs of services) {
    if (cs.id) byServiceId.set(cs.id, cs)
    const item = unwrapServiceItem(cs.service_item)
    if (item?.id) byItemId.set(item.id, { ...item, name: item.name || cs.service_name || item.name })
    if (cs.service_item_id && item) byItemId.set(cs.service_item_id, item)
    const names = [cs.service_name, item?.name].map(normalizeName).filter(Boolean)
    for (const n of names) {
      if (!byName.has(n) && item) byName.set(n, { ...item, name: item.name || cs.service_name })
    }
  }

  const resolved: ServiceItemWorkFields[] = []
  const seen = new Set<string>()

  const push = (item: ServiceItemWorkFields | null | undefined) => {
    if (!item) return
    const key = item.id || normalizeName(item.name) || JSON.stringify(item.default_checklist)
    if (!key || seen.has(key)) return
    seen.add(key)
    resolved.push(item)
  }

  for (const line of selected) {
    if (line.service_item_id && byItemId.has(line.service_item_id)) {
      push(byItemId.get(line.service_item_id))
      continue
    }
    if (line.contract_service_id && byServiceId.has(line.contract_service_id)) {
      const cs = byServiceId.get(line.contract_service_id)!
      const item = unwrapServiceItem(cs.service_item)
      if (item) {
        push({ ...item, name: item.name || cs.service_name || item.name })
      } else if (cs.service_name) {
        push({ name: cs.service_name, default_checklist: [] })
      }
      continue
    }
    const descKey = normalizeName(line.description)
    if (descKey && byName.has(descKey)) {
      push(byName.get(descKey))
    }
  }

  return resolved
}

/** Compose "{name}: {work_description}" sections for job.description. */
export function composeWorkDescriptionFromItems(
  items: ServiceItemWorkFields[] | null | undefined
): string {
  if (!Array.isArray(items) || items.length === 0) return ''
  return items
    .map((item) => {
      const desc = String(item.work_description || '').trim()
      if (!desc) return null
      const name = String(item.name || 'Service').trim() || 'Service'
      return `${name}: ${desc}`
    })
    .filter((s): s is string => !!s)
    .join('\n\n')
}

/** Build checklist rows with "{name}: {task}" prefixes. */
export function checklistDraftFromItems(
  items: ServiceItemWorkFields[] | null | undefined
): ChecklistTemplateItem[] {
  if (!Array.isArray(items) || items.length === 0) return []
  const rows: ChecklistTemplateItem[] = []
  for (const item of items) {
    const name = String(item.name || 'Service').trim() || 'Service'
    const tasks = normalizeChecklistItems(item.default_checklist)
    for (const task of tasks) {
      rows.push({
        label: `${name}: ${task.label}`,
        sort_order: rows.length,
        photo_required: !!task.photo_required,
      })
    }
  }
  return normalizeChecklistItems(rows)
}
